// src/components/coins/CoinReward.js
//
// The "You earned 🪙 +23 coins!" card on a game's Final Score screen.
//
// Asks the awardGameCoins Cloud Function ONCE when it appears (a remount or
// a double render can't pay twice — the function also only pays full coins
// once per topic per day). The coins then fly from this card into the coin
// counter. If coins aren't switched on yet, or the request fails, the card
// simply doesn't show: the game's own Continue button is never affected.

import React, { useEffect, useRef, useState } from "react";
import { awardGameCoins } from "../../services/coinService";
import { Coin } from "./CoinCounter";
import "./coins.css";

const paidThisVisit = new Set(); // slug|score — guards React double-mounts

const CoinReward = ({ slug, title, quizScore = 0, bonusScore = 0, correct = null }) => {
  const [result, setResult] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const key = `${slug}|${quizScore}|${bonusScore}`;
    if (!slug || paidThisVisit.has(key)) return;
    paidThisVisit.add(key);
    let alive = true;
    (async () => {
      const r = ref.current?.getBoundingClientRect();
      const from = r && r.width ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : undefined;
      const out = await awardGameCoins({ slug, title, quizScore, bonusScore, correct, from });
      if (alive) setResult(out || { amount: 0, why: "off" });
    })();
    return () => {
      alive = false;
    };
  }, [slug, title, quizScore, bonusScore, correct]);

  if (!result) {
    return (
      <div className="sbl-coin-reward is-waiting" ref={ref}>
        <Coin size={34} className="is-spin" />
        <span>Counting your coins…</span>
      </div>
    );
  }
  if (result.why === "off") return <div ref={ref} />;

  return (
    <div className={`sbl-coin-reward ${result.amount > 0 ? "is-paid" : "is-none"}`} ref={ref}>
      <Coin size={40} className={result.amount > 0 ? "is-bump" : ""} />
      {result.amount > 0 ? (
        <div>
          <strong>You earned {result.amount} coins!</strong>
          <span>
            {result.why === "replay"
              ? "Played again today — full coins come back tomorrow."
              : result.why === "daily-cap"
              ? "That's today's coin limit from games. More tomorrow!"
              : "Spend them in the shop soon."}
          </span>
        </div>
      ) : result.why === "trial-ended" ? (
        <div>
          <strong>Great playing!</strong>
          <span>Coins are resting now that the free trial is over — your avatar and coins are still yours. Ask a grown-up about a plan to earn more.</span>
        </div>
      ) : (
        <div>
          <strong>Today's game coins are all collected!</strong>
          <span>Come back tomorrow for more.</span>
        </div>
      )}
    </div>
  );
};

export default CoinReward;
